import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";

/**
 * AddEventModal — quick "schedule on a deal" action from the home page.
 *
 * Find an existing deal, then set its adjuster-meeting date/time OR build date
 * so it shows up on the week schedule. Deliberately lightweight: it sets the
 * date column + writes an activity_log entry. It does NOT force a stage
 * transition (that flow lives on the deal page / dispatch board), so adding an
 * event from home never jumps a deal to an unexpected stage.
 *
 * Props:
 *  onClose    — dismiss the modal
 *  onCreated  — called after a successful save (close + reload the schedule)
 *  actorName  — logged-in user's full name, for the activity description
 */

interface AddEventModalProps {
  onClose: () => void;
  onCreated: () => void;
  actorName: string | null;
}

type EventType = "meet" | "build";

interface DealHit {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  stage: string | null;
}

const addrOf = (d: DealHit) =>
  [d.property_street, d.property_city].filter(Boolean).join(", ");

export default function AddEventModal({ onClose, onCreated, actorName }: AddEventModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<DealHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<DealHit | null>(null);

  const [eventType, setEventType] = useState<EventType>("meet");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // debounced deal search by homeowner name / address
  useEffect(() => {
    if (selected) return; // don't search once a deal is picked
    const q = query.trim();
    if (q.length < 2) { setHits([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const like = `%${q}%`;
      const { data } = await supabase
        .from("deals")
        .select("id,homeowner_name,property_street,property_city,stage")
        .or(`homeowner_name.ilike.${like},property_street.ilike.${like},property_city.ilike.${like}`)
        .order("created_at", { ascending: false })
        .limit(12);
      if (cancelled) return;
      setHits((data ?? []) as DealHit[]);
      setSearching(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, selected]);

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose();
  }

  async function handleSave() {
    if (!selected || !date) return;
    setSaving(true);
    setErr(null);

    const updates: Record<string, string> =
      eventType === "meet"
        ? { adjuster_meeting_date: `${date}T${time || "09:00"}:00` }
        : { build_scheduled_date: date };

    const { error } = await supabase.from("deals").update(updates).eq("id", selected.id);
    if (error) { setErr(error.message); setSaving(false); return; }

    const label = eventType === "meet" ? "Adjuster meeting" : "Build";
    const whenTxt = eventType === "meet" ? `${date} ${time || "09:00"}` : date;
    await supabase.from("activity_log").insert({
      deal_id: selected.id,
      activity_type: "schedule",
      title: `${label} scheduled`,
      description: `${label} set for ${whenTxt} by ${actorName ?? "staff"} (from home).`,
      metadata: { event_type: eventType, ...updates, source: "home_add_event" },
    }).then(() => {}, () => {}); // best-effort — never block on the log

    onCreated();
  }

  const canSave = !!selected && !!date && !saving;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div>
            <h2 className="text-sm font-semibold text-bardin-ink">Add Event</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Schedule an adjuster meeting or build on a deal.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {/* Deal picker */}
          {!selected ? (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Find a deal</label>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Homeowner name or address…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
              {searching && <div className="text-xs text-slate-400 mt-2">Searching…</div>}
              {!searching && query.trim().length >= 2 && hits.length === 0 && (
                <div className="text-xs text-slate-400 mt-2">No deals found.</div>
              )}
              {hits.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
                  {hits.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => { setSelected(d); setHits([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50"
                    >
                      <div className="text-sm font-semibold text-bardin-ink truncate">{d.homeowner_name ?? "(no name)"}</div>
                      {addrOf(d) && <div className="text-[11px] text-slate-500 truncate">📍 {addrOf(d)}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-bardin-ink truncate">{selected.homeowner_name ?? "(no name)"}</div>
                {addrOf(selected) && <div className="text-[11px] text-slate-500 truncate">📍 {addrOf(selected)}</div>}
              </div>
              <button
                onClick={() => { setSelected(null); setQuery(""); }}
                className="text-[11px] font-semibold text-bardin-teal shrink-0"
              >Change</button>
            </div>
          )}

          {/* Event type + date/time (only once a deal is picked) */}
          {selected && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Event type</label>
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs font-bold w-full">
                  <button
                    onClick={() => setEventType("meet")}
                    className={"flex-1 px-3 py-2 " + (eventType === "meet" ? "bg-bardin-teal text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
                  >🏠 Adjuster meeting</button>
                  <button
                    onClick={() => setEventType("build")}
                    className={"flex-1 px-3 py-2 border-l border-slate-200 " + (eventType === "build" ? "bg-bardin-orange text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
                  >🔨 Build</button>
                </div>
              </div>

              <div className={"grid gap-3 " + (eventType === "meet" ? "grid-cols-2" : "grid-cols-1")}>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                {eventType === "meet" && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Time</label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 rounded-xl bg-bardin-orange text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition shadow-sm"
          >{saving ? "Saving…" : "Add event"}</button>
        </div>
      </div>
    </div>
  );
}
